/**
 * Point Draw helpers for circular-junction spoke connections.
 *
 * Junction ports may face any angle (4 / 6 / 8-way). Cardinal ports keep the
 * existing Manhattan L-approach; non-cardinal ports are reached along the spoke
 * ray so diagonal (and other) legs can leave / enter the hub.
 *
 * Port-to-port diagonal connections use outbound stubs + a Manhattan bridge so
 * facing `/` and `\` ports get a short spoke-out + horizontal (or L) link
 * instead of projecting onto the target ray (which produces a deep V).
 */

export type Vec = { x: number; y: number };

const EPS = 1e-6;

export const normDeg = (deg: number): number => ((deg % 360) + 360) % 360;

export const angDiffDeg = (a: number, b: number): number => {
  let d = Math.abs(normDeg(a - b));
  if (d > 180) d = 360 - d;
  return d;
};

/** Nearest multiple of 45° in [0, 360). */
export const nearestAxis45 = (deg: number): number =>
  normDeg(Math.round(deg / 45) * 45);

export const isCardinalDeg = (deg: number, tol = 1): boolean => {
  const n = normDeg(deg);
  return [0, 90, 180, 270].some((c) => angDiffDeg(n, c) <= tol);
};

/**
 * Sort comparator ranking which spoke of a junction a run should connect to:
 * cardinal spokes first, then nearest.
 *
 * Cardinal spokes outrank diagonal ones regardless of distance. Only a cardinal
 * arrival keeps the run Manhattan, which is what lets the shared generator
 * fillet the corner with a fixed-radius curve — a diagonal spoke can only be
 * welded on as a raw angled straight. Distance alone is a poor guide because a
 * hub's diagonal tips reach further out than its flat ones (the tip sits on the
 * bounding box edge, so a 45° spoke extends to a corner), which puts the
 * diagonal nearest the cursor for most approach angles.
 *
 * Callers must classify `cardinal` with the same tolerance
 * {@link routeHeadToSpokePort} uses to pick its Manhattan branch, so a spoke
 * ranked highly here is always one that actually routes orthogonally. Ranking
 * rather than filtering keeps diagonal spokes reachable as a fallback when no
 * cardinal one can be approached legally.
 */
export function compareSpokePorts(
  a: { cardinal: boolean; distance: number },
  b: { cardinal: boolean; distance: number }
): number {
  if (a.cardinal !== b.cardinal) return a.cardinal ? -1 : 1;
  return a.distance - b.distance;
}

/**
 * A pick counts as deliberate when the cursor is at least this many times
 * nearer to one spoke than to the next one. Below the margin the two tips are
 * competing for the same click and the choice is treated as an aim at the hub.
 *
 * Expressed as a ratio rather than a distance so it holds at any zoom and for
 * any hub size, and needs no coupling to the grid or the snap radius.
 */
const DELIBERATE_SPOKE_RATIO = 2;

/**
 * Order a hub's spokes for a connection, best first.
 *
 * Two intents share one gesture here. Pointing squarely at a spoke names that
 * spoke, and must be honoured even when it is diagonal — otherwise the corner
 * ports of an 8-way hub become impossible to connect to. Pointing loosely at the
 * hub names no spoke, and there {@link compareSpokePorts} applies: the flat
 * ports come first because only a cardinal arrival can be filleted into a curve,
 * and a hub's diagonal tips reach further out so they win on raw distance for
 * most approach angles.
 *
 * The two are separated by how decisively the nearest spoke beats its closest
 * rival, which is what distinguishes aiming at a tip from drifting between two.
 * The full list is always returned so the caller can fall through when the
 * preferred spoke cannot be approached legally.
 */
export function rankSpokePorts<T extends { cardinal: boolean; distance: number }>(
  spokes: readonly T[]
): T[] {
  const byDistance = [...spokes].sort((a, b) => a.distance - b.distance);
  const [nearest, runnerUp] = byDistance;
  if (!nearest) return [];
  const deliberate =
    !runnerUp || nearest.distance * DELIBERATE_SPOKE_RATIO <= runnerUp.distance;
  if (deliberate) {
    return [nearest, ...byDistance.slice(1).sort(compareSpokePorts)];
  }
  return byDistance.sort(compareSpokePorts);
}

export function pointAlongHeading(origin: Vec, headingDeg: number, dist: number): Vec {
  const rad = (normDeg(headingDeg) * Math.PI) / 180;
  return {
    x: origin.x + Math.cos(rad) * dist,
    y: origin.y + Math.sin(rad) * dist,
  };
}

/**
 * Project `cursor` onto the outward spoke ray from `origin`.
 * Returns null when the cursor lies on the hub side of the tip (behind the port).
 */
export function projectOntoSpokeRay(
  origin: Vec,
  outwardDeg: number,
  cursor: Vec
): Vec | null {
  const rad = (normDeg(outwardDeg) * Math.PI) / 180;
  const ox = Math.cos(rad);
  const oy = Math.sin(rad);
  const dx = cursor.x - origin.x;
  const dy = cursor.y - origin.y;
  const along = dx * ox + dy * oy;
  if (along <= EPS) return null;
  return { x: origin.x + ox * along, y: origin.y + oy * along };
}

/** Axis-aligned corner(s) from `a` to `b` (empty when already collinear on an axis). */
function manhattanCorners(a: Vec, b: Vec): Vec[] {
  if (Math.abs(a.x - b.x) <= EPS || Math.abs(a.y - b.y) <= EPS) return [];
  // Prefer the longer span as a single run — facing diagonal stubs often share a
  // row/column and collapse to a pure horizontal (or vertical) with no corner.
  if (Math.abs(a.x - b.x) >= Math.abs(a.y - b.y)) {
    return [{ x: b.x, y: a.y }];
  }
  return [{ x: a.x, y: b.y }];
}

function pushUnique(wps: Vec[], p: Vec): void {
  const last = wps[wps.length - 1];
  if (last && Math.hypot(p.x - last.x, p.y - last.y) <= EPS) return;
  wps.push(p);
}

/**
 * Connect two junction spoke ports with outbound stubs + a Manhattan bridge.
 *
 * Returns waypoints *after* the source tip (ending on the target tip), or null
 * when both ports are cardinal (caller should use the Manhattan L approach) or
 * the geometry cannot form a useful bridge.
 *
 * `headIsStub`: when true, `source.tip` is already the outer end of a spoke leg
 * (user drew away from the hub first) — do not extend another outbound stub.
 */
export function routeBetweenSpokePorts(
  source: { tip: Vec; outwardDeg: number; headIsStub?: boolean },
  target: { tip: Vec; outwardDeg: number },
  stubLen: number
): Vec[] | null {
  const srcOut = normDeg(source.outwardDeg);
  const tgtOut = normDeg(target.outwardDeg);
  if (isCardinalDeg(srcOut) && isCardinalDeg(tgtOut)) return null;

  const sLen = Math.max(stubLen, EPS);
  const tipDist = Math.hypot(target.tip.x - source.tip.x, target.tip.y - source.tip.y);
  // Same hub / overlapping tips — keep the single-port approach instead.
  if (tipDist < sLen * 2) return null;

  const stubA = source.headIsStub
    ? { x: source.tip.x, y: source.tip.y }
    : pointAlongHeading(source.tip, srcOut, sLen);
  const stubB = pointAlongHeading(target.tip, tgtOut, sLen);

  // Bridge must have some length; coincident stubs mean the ports are too close.
  if (Math.hypot(stubB.x - stubA.x, stubB.y - stubA.y) <= EPS) return null;

  const wps: Vec[] = [];
  pushUnique(wps, stubA);
  for (const c of manhattanCorners(stubA, stubB)) pushUnique(wps, c);
  pushUnique(wps, stubB);
  pushUnique(wps, { x: target.tip.x, y: target.tip.y });
  return wps.length > 0 ? wps : null;
}

/** Nearest multiple of 90° in [0, 360). */
const nearestCardinal = (deg: number): number => normDeg(Math.round(deg / 90) * 90);

/**
 * Run `head` along `heading` until it meets the port's outward spoke ray.
 * Returns null unless the meeting point is ahead of the head *and* outside the
 * hub (on the outward side of the tip).
 */
function forwardSpokeIntersection(
  head: Vec,
  heading: number,
  target: { tip: Vec; outwardDeg: number }
): { waypoints: Vec[]; spokeLength: number } | null {
  const headingRad = (normDeg(heading) * Math.PI) / 180;
  const outwardRad = (normDeg(target.outwardDeg) * Math.PI) / 180;
  const forward = { x: Math.cos(headingRad), y: Math.sin(headingRad) };
  const outward = { x: Math.cos(outwardRad), y: Math.sin(outwardRad) };
  const delta = { x: target.tip.x - head.x, y: target.tip.y - head.y };

  const cross = (a: Vec, b: Vec) => a.x * b.y - a.y * b.x;
  const denominator = cross(forward, outward);
  if (Math.abs(denominator) <= EPS) return null;

  const forwardDistance = cross(delta, outward) / denominator;
  const spokeLength = cross(delta, forward) / denominator;
  if (forwardDistance <= EPS || spokeLength <= EPS) return null;

  return {
    waypoints: [
      {
        x: head.x + forward.x * forwardDistance,
        y: head.y + forward.y * forwardDistance,
      },
      { ...target.tip },
    ],
    spokeLength,
  };
}

/**
 * Connect a head to a diagonal junction port by running on a cardinal axis and
 * turning onto the spoke only for the last short leg.
 *
 * This replaces orthogonally projecting `head` onto the infinite spoke ray,
 * which places the turn far away from the hub and draws a deep V.
 *
 * `travelHeading` is the direction already being travelled; pass null when the
 * run has no committed direction yet (a lone first vertex), in which case every
 * cardinal axis is tried and the one with the shortest spoke leg wins.
 */
export function routeCardinalHeadToDiagonalPort(
  head: Vec,
  travelHeading: number | null,
  target: { tip: Vec; outwardDeg: number }
): Vec[] | null {
  if (isCardinalDeg(target.outwardDeg)) return null;

  const headings =
    travelHeading != null && isCardinalDeg(travelHeading)
      ? [nearestCardinal(travelHeading)]
      : [0, 90, 180, 270];

  let best: { waypoints: Vec[]; spokeLength: number } | null = null;
  for (const heading of headings) {
    const solved = forwardSpokeIntersection(head, heading, target);
    if (!solved) continue;
    if (!best || solved.spokeLength < best.spokeLength) best = solved;
  }
  return best ? best.waypoints : null;
}

/**
 * Plan waypoints from head `H` to port tip `P` so the final leg arrives along
 * `arrivalHeading` (direction of travel into the tip = opposite of outward spoke).
 *
 * Cardinal arrivals use a Manhattan L from the outward half-plane.
 * Non-cardinal arrivals stay on the spoke ray (optional lateral drop onto the ray).
 */
export function routeHeadToSpokePort(
  H: Vec,
  arrivalHeading: number,
  P: Vec
): Vec[] | null {
  const arrival = normDeg(arrivalHeading);
  const eps = EPS;
  const wps: Vec[] = [];

  if (isCardinalDeg(arrival)) {
    const heading = nearestAxis45(arrival);
    if (heading === 0 || heading === 180) {
      const okSide = heading === 0 ? H.x < P.x - eps : H.x > P.x + eps;
      if (!okSide) return null;
      if (Math.abs(H.y - P.y) > eps) wps.push({ x: H.x, y: P.y });
      wps.push({ x: P.x, y: P.y });
      return wps;
    }
    const okSide = heading === 90 ? H.y < P.y - eps : H.y > P.y + eps;
    if (!okSide) return null;
    if (Math.abs(H.x - P.x) > eps) wps.push({ x: P.x, y: H.y });
    wps.push({ x: P.x, y: P.y });
    return wps;
  }

  // Non-cardinal: approach along the spoke from the outward side.
  const outward = normDeg(arrival + 180);
  const rad = (outward * Math.PI) / 180;
  const ox = Math.cos(rad);
  const oy = Math.sin(rad);
  const dx = H.x - P.x;
  const dy = H.y - P.y;
  const along = dx * ox + dy * oy;
  if (along <= eps) return null;

  const lat = -dx * oy + dy * ox;
  if (Math.abs(lat) > eps) {
    wps.push({ x: P.x + ox * along, y: P.y + oy * along });
  }
  wps.push({ x: P.x, y: P.y });
  return wps;
}

/** True when every consecutive segment in `points` is axis-aligned. */
export function isManhattanPolyline(points: Vec[]): boolean {
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    const dx = Math.abs(b.x - a.x);
    const dy = Math.abs(b.y - a.y);
    const axis = (dx <= EPS && dy > EPS) || (dy <= EPS && dx > EPS);
    if (!axis) return false;
  }
  return true;
}

/** Heading of segment a→b snapped to the nearest 45° (supports diagonal spokes). */
export function spokeSegHeading(a: Vec, b: Vec): number {
  return nearestAxis45((Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI);
}
